import { useAuthManager } from "~/composables/auth/useAuthManager";
import { useAuthModal } from "~/composables/auth/useAuthModal";
import { logger } from "~/utils/logger";

export default defineNuxtRouteMiddleware(async (to) => {
  const path = to.path || "";
  const requiresAuth =
    Boolean(to.meta?.requiresAuth) ||
    path.startsWith("/dashboard") ||
    path.startsWith("/admin") ||
    path.startsWith("/booking-manager");

  // For SSR, only do basic checks - let client hydration handle full auth
  // Don't redirect on SSR if user state is unavailable (prevents false redirects on refresh)
  if (process.server) {
    if (!requiresAuth) return;

    const ssrUser = useState("authUser").value as any | null;

    // Only redirect if we explicitly know there's no auth cookie at all
    // If ssrUser is null but we have cookies, let client-side handle it
    const event = useRequestEvent();
    const cookies = event?.node?.req?.headers?.cookie || "";
    const hasSessionCookie = cookies.includes("ory_kratos_session");

    // If there's a session cookie, let client-side auth handle verification
    // This prevents false redirects on page refresh when SSR auth fetch fails
    if (hasSessionCookie) {
      logger.log(
        "[00.auth.global SSR] Session cookie found, allowing client-side verification",
      );
      return;
    }

    // Only redirect if definitely no session
    if (!ssrUser && !hasSessionCookie) {
      logger.log("[00.auth.global SSR] No session found, redirecting to login");
      return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
    }

    // Role-aware SSR guard (only if we have user data)
    if (ssrUser) {
      if (path.startsWith("/admin") && ssrUser?.role !== "ADMIN") {
        return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
      }

      if (
        path.startsWith("/booking-manager") &&
        ssrUser?.role !== "BOOKING_MANAGER"
      ) {
        return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
      }
    }

    return;
  }

  const { ensureAuthenticated } = useAuthManager();
  logger.log("[00.auth.global] Route:", to.path, "requiresAuth:", requiresAuth);

  if (requiresAuth) {
    try {
      // Ensure the client has a valid authenticated session
      logger.log("[00.auth.global] Calling ensureAuthenticated...");
      const isAuthenticated = await ensureAuthenticated();
      logger.log(
        "[00.auth.global] ensureAuthenticated returned:",
        isAuthenticated,
      );

      if (!isAuthenticated) {
        // Check if we're on a booking page
        const isBookingPage = to.path?.startsWith("/booking");

        if (isBookingPage) {
          // Open auth modal instead of navigating away
          const { openAuthModal } = useAuthModal();
          openAuthModal("signin", to.fullPath);
          return false; // Prevent navigation
        } else {
          // For other pages, redirect to sign-in page with redirect-back
          logger.log(
            "[00.auth.global] Not authenticated, redirecting to /login",
          );
          return navigateTo(
            `/login?redirect=${encodeURIComponent(to.fullPath)}`,
          );
        }
      }
    } catch (error: any) {
      logger.log("[00.auth.global] Caught error:", error?.message);
      // If user needs verification, redirect to verification page
      if (error?.message === "VERIFICATION_REDIRECT") {
        logger.log("[00.auth.global] Redirecting to /verification");
        // Mark that user just registered and needs verification
        if (typeof window !== "undefined") {
          logger.log("[00.auth.global] 🏷️ SETTING from_registration flag");
          localStorage.setItem("from_registration", "true");
          logger.log(
            "[00.auth.global] ✅ Flag set:",
            localStorage.getItem("from_registration"),
          );
        }
        return navigateTo("/verification");
      }
      // For other errors, redirect to sign-in with redirect-back
      logger.log("[00.auth.global] Unknown error, redirecting to /login");
      return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
    }
  }
});
